import {Injectable} from '@angular/core';
import {PartyService} from "@portal/api/party-service";
import {PartySettings} from "@portal/api/requests/parties/party-settings-update-request";
import {AuthService} from "../../core/auth/auth.service";
import {
    BehaviorSubject,
    debounceTime,
    filter,
    merge,
    of,
    shareReplay,
    startWith,
    switchMap,
    tap
} from "rxjs";
import {StorageService} from "@portal/shared/services/storage.service";
import {OrganizationSelectorService} from "@portal/shared/services/organization-selector.service";
import {PartySelectedService} from "@portal/shared/services/party-selected.service";
import {Router} from "@angular/router";
import {SearchRequest} from "@portal/api/requests/search-request";

@Injectable({
    providedIn: 'root',
})
export class PartySettingsService {
    private readonly currentPartySettingsKey = 'partySettings';

    private currentPartySettings = new BehaviorSubject<PartySettings>(null);
    private currentPartyEditable = new BehaviorSubject<boolean>(null);

    get isPartyEditable() {
        return this.currentPartySettings.getValue()?.isEditable;
    }

    get isTrial() {
        return this.currentPartySettings.getValue()?.isTrial;
    }

    get daysToExpire() {
        return this.currentPartySettings.getValue()?.daysToExpire;
    }

    get currentPartyEditable$() {
        return this.currentPartyEditable.asObservable().pipe(filter(item => item != null));
    }

    get currentPartySettings$() {
        return this.currentPartySettings.asObservable().pipe(
            // forces a start on null so it can ignore equal values from the storage
            // and never repeat emissions with same value
            startWith(null),
            shareReplay(),
            tap(value => {
                this.currentPartyEditable.next(value?.isEditable);
            }),
            filter(item => {
                if (item === null && item !== this.currentPartySettings.getValue()) {
                    return true;
                }
                return !(this.currentPartySettings.getValue()?.isEditable === item?.isEditable &&
                    this.currentPartySettings.getValue()?.hasDonationsValueVisible === item?.hasDonationsValueVisible &&
                    this.currentPartySettings.getValue()?.hasSponsorsValueVisible === item?.hasSponsorsValueVisible);
            }));
    }

    constructor(private _partyService: PartyService,
                private _storageService: StorageService,
                private _organizationSelectorService: OrganizationSelectorService,
                private _partySelectedService: PartySelectedService,
                private _authService: AuthService,
                private _router: Router) {
        this.loadFromStorage();

        merge(
            this._partySelectedService.currentParty,
            this._authService.loginStatus.pipe(filter(isLoggedIn => isLoggedIn))
        ).pipe(debounceTime(100)).subscribe(() => {
            this.loadCurrentPartySettings();
        });
    }

    private loadFromStorage() {
        const settings = this._storageService.getObject(this.currentPartySettingsKey);
        if (settings) {
            this.currentPartyEditable.next(settings.isEditable);
            this.currentPartySettings.next(settings);
        }
    }

    loadCurrentPartySettings() {
        const partyId = this._partySelectedService.getPartyId();
        const orgId = this._organizationSelectorService.org?.id ?? this._storageService.getOrg()?.id;

        if (this._authService.isAuthenticated && partyId) {
            this._partyService.getUpdateSettings().pipe(
                switchMap(({data}) => {
                    if (!data?.isLocked || !orgId) {
                        return of({ settings: data, shouldResetParty: false });
                    }

                    return this._partyService.getUserParties({
                        page: 0,
                        pageSize: 100,
                        sortDirection: 1,
                        sortField: 'score'
                    } as SearchRequest).pipe(
                        switchMap(({data: parties}) => {
                            const selectedParty = parties?.find(party => String(party.id) === String(partyId));
                            const shouldResetParty = !selectedParty?.hasAccess;
                            return of({ settings: data, shouldResetParty });
                        })
                    );
                })
            ).subscribe(({settings, shouldResetParty}) => {
                if (shouldResetParty) {
                    this.resetSelectedPartyAndRedirect();
                    return;
                }

                const {hasDonationsValueVisible, isEditable, hasSponsorsValueVisible, daysToExpire, isTrial, isLocked} = settings;
                this.setSettings(hasDonationsValueVisible, isEditable, hasSponsorsValueVisible, daysToExpire, isTrial, isLocked);
            });
        }
    }

    private setSettings(hasDonationsValueVisible, isEditable, hasSponsorsValueVisible,daysToExpire,isTrial, isLocked) {
        const currentSettings = {
            hasDonationsValueVisible,
            isEditable,
            hasSponsorsValueVisible,
            daysToExpire,
            isTrial,
            isLocked
        };

        this.currentPartySettings.next(currentSettings);
        this.currentPartyEditable.next(currentSettings.isEditable);
        this._storageService.setObject(this.currentPartySettingsKey, currentSettings);
    }

    private resetSelectedPartyAndRedirect() {
        this.currentPartySettings.next(null);
        this.currentPartyEditable.next(null);
        this._storageService.remove('party');
        this._storageService.remove(this.currentPartySettingsKey);
        this._partySelectedService.resetParty();
        this._router.navigate(['/parties']);
    }
}
